import React, { useState, useContext, useMemo } from 'react'
import { MInputTextField, LoadingContext, MButton, BufferService } from 'src/M_Components'
import { Clear, Done } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import './settings.css'
import { useLocation } from 'react-router-dom'
import { STORAGE_CARRIER } from 'src/components/auth/storageKeys'

// Helpers to reliably detect API failures and extract a user-friendly message
// If your project has a different path for these utilities, adjust the import accordingly.
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse'

/**
 * Realm-aware Change Password:
 * - USER realm (/home/...):
 *    -> Calls `Users/Update` with body: { password }
 *    -> Uses the user id from localStorage('userId') as you already do.
 *
 * - CARRIER realm (/carrier/...):
 *    -> Calls `CarrierIdentity/password/change` (POST)
 *    -> Body:
 *       {
 *         "carrierLoginId": "string",   // read from localStorage(STORAGE_CARRIER.session).CarrierLoginId
 *         "currentPassword": "string",  // from OldPassword field
 *         "newPassword": "string"       // from NewPassword field
 *       }
 *
 * UI stays the same for both realms (Old / New / Confirm), we just switch API + payload.
 */
export default function ChangePassword({ Email }) {
  const ctx = useContext(LoadingContext)
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const IS_CARRIER = pathname.startsWith('/carrier')

  // Keep your same Map-based state pattern
  const [values, setValues] = useState(new Map([]))

  // Resolve baseUrl via context at top-level (safe hook usage)
  const baseUrl = ctx.DEFAULTURL;

  // Pre-create the proper services (memoized once)
  // create services with baseUrl so BufferService does NOT need to call any hook internally
  const passwordServiceUser = useMemo(
    () => new BufferService('Users/Update', baseUrl),
    [baseUrl]
  );

  const passwordServiceCarrier = useMemo(
    () => new BufferService('CarrierIdentity/password/change', baseUrl),
    [baseUrl]
  );

  const changeValues = (val, key) => {
    values.set(key, val)
    setValues(new Map(values))
  }

  // Helper: safely extract CarrierLoginId from the session stored at login
  const getCarrierLoginIdFromStorage = () => {
    try {
      const raw = localStorage.getItem(STORAGE_CARRIER.session)
      if (!raw) return null
      const session = JSON.parse(raw)
      // Back-end payload shows "CarrierLoginId" (pascal case) in the Session object
      return session?.CarrierLoginId ?? session?.carrierLoginId ?? null
    } catch {
      return null
    }
  }

  // Build a friendly error -> toast pipeline using our helpers
  const toastApiError = (res, fallbackKey = 'LabelTryAgain') => {
    const msg = apiMessage(res, t(fallbackKey))
    ctx.toastMessages('error', msg, '')
  }

  const submitPass = async () => {
    ctx.setIsLoad(true)

    // Shared validations (apply to both realms; same UI)
    const oldPass = values.get('pass') || ''
    const newPass = values.get('newPass') || ''
    const confNew = values.get('confNewPass') || ''

    if (newPass === oldPass) {
      ctx.toastMessages('error', t('PasswordcannotSame'), '')
      ctx.setIsLoad(false)
      return
    }
    if (newPass !== confNew) {
      ctx.toastMessages('error', t('NewPasswordNotMatch'), '')
      ctx.setIsLoad(false)
      return
    }

    try {
      // -------------------- CARRIER REALM --------------------
      if (IS_CARRIER) {
        const carrierLoginId = getCarrierLoginIdFromStorage()
        if (!carrierLoginId) {
          ctx.toastMessages('error', t('LabelTryAgain'), '')
          return
        }

        const payload = {
          carrierLoginId: String(carrierLoginId),
          currentPassword: oldPass,
          newPassword: newPass
        }

        const res = await passwordServiceCarrier.post(payload)

        // Use the shared helper to detect failure across shapes (AxiosError, {succeeded:false}, etc.)
        if (isFailurePayload(res)) {
          toastApiError(res, 'LabelTryAgain')
          return
        }

        ctx.toastMessages('success', '', t('SuccessUpdatePassword'))
        setValues(new Map())
        return
      }

      // ---------------------- USER REALM ----------------------
      // Keeps the existing user flow (no need to send old password to your current API).
      const id = localStorage.getItem('userId') || ''
      const body = { password: newPass }

      const res = await passwordServiceUser.update(id, body)

      if (isFailurePayload(res)) {
        toastApiError(res, 'LabelTryAgain')
        return
      }

      ctx.toastMessages('success', '', t('SuccessUpdatePassword'))
      setValues(new Map())
    } catch (err) {
      // Catch unexpected exceptions (network, parsing, etc.)
      toastApiError(err, 'LabelTryAgain')
    } finally {
      // Always stop loading, even when an exception bubbles out
      ctx.setIsLoad(false)
    }
  }

  return (
    <div className='divGlobalSettings' data-testid='settings-container'>
      <div className='subTitleSettings'>
        <div>
          <div style={{ color: ctx.secondaryColor }} data-testid='change-password-title'>
            {t('ChangePassword')}
          </div>
          <div className='labelSubtitleSettings' data-testid='change-password-info'>
            {t('InfoChangePassword')}
          </div>
        </div>
      </div>

      <div className='changePrincipalDiv'>
        <div className='full-width'>
          <MInputTextField
            label={t('OldPassword')}
            value={values.get('pass') || ''}
            type='password'
            onChange={(e) => changeValues(e.target.value, 'pass')}
            style={{ width: '100%' }}
          />
        </div>

        <MInputTextField
          label={t('NewPassword')}
          value={values.get('newPass') || ''}
          type='password'
          onChange={(e) => changeValues(e.target.value, 'newPass')}
          passVerification
          minLengthverification={6}
          valueAgain={values.get('confNewPass')}
        />

        <MInputTextField
          label={t('ConfirmPassword')}
          value={values.get('confNewPass') || ''}
          type='password'
          onChange={(e) => changeValues(e.target.value, 'confNewPass')}
        />

        <div className='buttonContainer'>
          <MButton
            label={t('ButtonCancel')}
            icon={<Clear style={{ color: 'var(--color-white)' }} />}
            backgroundColor={'var(--color-red)'}
            labelColor={'var(--color-white)'}
            onClick={() => { setValues(new Map([])) }}
          />
          <MButton
            label={t('ButtonConfirm')}
            icon={<Done style={{ color: 'var(--color-white)' }} />}
            backgroundColor={'var(--color-green)'}
            labelColor={'var(--color-white)'}
            onClick={submitPass}
          />
        </div>
      </div>
    </div>
  )
}

ChangePassword.propTypes = {
  Email: PropTypes.string
}
